import { useEffect, useState } from "react";
import { NavLink, useNavigate, useMatch } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useStreak } from "@/context/StreakContext";
import { api } from "@/lib/api";
import { LayoutDashboard, BookOpen, Brain, FileText, Calculator, Sigma, Trophy, Layers, Clock, LineChart, Search, LogOut, Menu, X, Sun, Moon, MessageSquare, Images, Zap, Sparkles, Crown, CalendarCheck, Flame, Shield, ShieldAlert, ShieldCheck, Network, Lock, Plus, Bookmark, BookOpenCheck, Users } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import CoachDrawer from "@/components/CoachDrawer";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/subjects", label: "Subjects", icon: BookOpen, testid: "nav-subjects" },
  { to: "/app/link-it", label: "Link It", icon: Network, testid: "nav-link-it", planGate: "link_it" },
  { to: "/app/flashcards", label: "Flashcards", icon: Layers, testid: "nav-flashcards" },
  { to: "/app/plan", label: "Weekly Plan", icon: CalendarCheck, testid: "nav-plan" },
  { to: "/app/coach/history", label: "Coach History", icon: MessageSquare, testid: "nav-coach-history" },
  { to: "/app/homework", label: "Homework", icon: Clock, testid: "nav-homework" },
  { to: "/app/decks", label: "My Decks", icon: Bookmark, testid: "nav-decks" },
  { to: "/app/questions", label: "Questions", icon: Brain, testid: "nav-questions" },
  { to: "/app/cars", label: "CARS Academy", icon: FileText, testid: "nav-cars" },
  { to: "/app/practice-test", label: "Test Section", icon: Trophy, testid: "nav-practice-test" },
  { to: "/app/math", label: "Math Lab", icon: Calculator, testid: "nav-math" },
  { to: "/app/equations", label: "Equations", icon: Sigma, testid: "nav-equations" },
  { to: "/app/reference", label: "Reference Charts", icon: BookOpenCheck, testid: "nav-reference" },
  { to: "/app/library", label: "Image Library", icon: Images, testid: "nav-library" },
  { to: "/app/community", label: "Community", icon: Users, testid: "nav-community" },
  { to: "/app/progress", label: "Progress", icon: LineChart, testid: "nav-progress" },
  { to: "/app/plans", label: "Plans", icon: Zap, testid: "nav-plans" },
  { to: "/app/feedback", label: "Feedback", icon: MessageSquare, testid: "nav-feedback" },
  { to: "/app/admin/dashboard", label: "Admin Dashboard", icon: ShieldCheck, testid: "nav-admin-dashboard", adminOnly: true },
  { to: "/app/admin/feedback", label: "Admin Inbox", icon: ShieldCheck, testid: "nav-admin-feedback", adminOnly: true },
  { to: "/app/admin/playground", label: "Playground Analytics", icon: ShieldCheck, testid: "nav-admin-playground", adminOnly: true },
];

const ADMIN_EMAIL = "picturemedllc@gmail.com";

function CreditChip() {
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/billing/me").then((r) => { if (mounted) setBilling(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    const onRefresh = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("pm-billing-refresh", onRefresh);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pm-billing-refresh", onRefresh);
    };
  }, []);
  if (!billing) return null;
  const isFree = billing.plan === "free";
  const Icon = isFree ? Sparkles : billing.plan === "pro" ? Crown : Zap;
  const accent = isFree ? "#6a7570" : billing.plan === "pro" ? "#c98a3b" : "#4f7942";
  const oneTime = !!billing.credits_one_time;
  // Show a "Buy credits" quick link when credits are running low.
  // Free (one-time): flag at <= 2 remaining. Paid: <= 25% of the bucket.
  const lowThreshold = oneTime ? 2 : Math.max(2, Math.floor((billing.credits_total || 0) * 0.25));
  const isLow = billing.credits_remaining <= lowThreshold;
  const chipLabel = oneTime
    ? `${billing.plan_name} — ${billing.credits_remaining} of ${billing.credits_total} one-time Open Topic Credits remaining. Tap for plans.`
    : `${billing.plan_name} — ${billing.credits_remaining} of ${billing.credits_total} Open Topic Credits remaining. Tap for plans.`;
  return (
    <div className="hidden sm:inline-flex items-center gap-1.5">
      <button
        onClick={() => nav("/app/plans")}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-transform hover:-translate-y-0.5"
        style={{ background: `${accent}18`, color: accent, border: `1.5px solid ${accent}55` }}
        data-testid="header-credit-chip"
        title={chipLabel}
      >
        <Icon size={12} />
        {(billing.credits_remaining % 1 === 0
          ? billing.credits_remaining
          : billing.credits_remaining.toFixed(1))}/{billing.credits_total} credits
        {isFree && <span className="ml-1 opacity-80">· Upgrade</span>}
      </button>
      {isLow && (
        <button
          onClick={() => nav("/app/plans#credit-packs")}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-transform hover:-translate-y-0.5"
          style={{ background: "#c98a3b18", color: "#c98a3b", border: "1.5px solid #c98a3b55" }}
          data-testid="header-buy-credits-btn"
          title="Buy a one-time credit top-up pack"
        >
          <Plus size={12} />
          Buy credits
        </button>
      )}
    </div>
  );
}

function StreakChip() {
  const { streak } = useStreak();
  if (!streak || !streak.current_streak) return null;
  const tier = streak.tier;
  const color = tier?.color || "#f59e0b";
  return (
    <div className="hidden md:inline-flex items-center gap-1.5">
      <div
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: `${color}18`, color, border: `1.5px solid ${color}55` }}
        data-testid="header-streak-chip"
        title={tier ? `${tier.name} — ${streak.current_streak}-day streak` : `${streak.current_streak}-day streak`}
      >
        <Flame size={12} />
        {streak.current_streak}
        {tier && <span className="ml-0.5">{tier.emoji}</span>}
      </div>
      <div
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
        style={{
          background: streak.freeze_available_this_week ? "var(--hl-blue)" : "var(--muted)",
          color: streak.freeze_available_this_week ? "var(--primary)" : "var(--text-muted)",
          border: `1.5px solid ${streak.freeze_available_this_week ? "var(--primary)" : "var(--border)"}44`,
        }}
        data-testid="header-freeze-chip"
        title={streak.freeze_available_this_week
          ? "Streak freeze available this week — miss 1 day and we'll auto-save your streak"
          : `Freeze used ${streak.freeze_used_this_week_on ? `on ${streak.freeze_used_this_week_on}` : "this week"}`}
      >
        <Shield size={11} />
        {streak.freeze_available_this_week ? "1" : "0"}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { pingStudy } = useStreak();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachTopic, setCoachTopic] = useState({ id: null, name: null });
  const [coachPrefill, setCoachPrefill] = useState("");
  const [features, setFeatures] = useState(null);
  const topicMatch = useMatch("/app/topic/:topicId");
  const nav = useNavigate();

  // Fetch this user's plan feature flags once so the nav can show lock icons
  // on Plus-only items (Link It, Full Test).
  useEffect(() => {
    api.get("/billing/features").then((r) => setFeatures(r.data)).catch(() => {});
  }, []);

  // When we hit a topic detail page, fetch the topic name for the coach
  // header. Falls back to global (topicId=null) on non-topic pages.
  useEffect(() => {
    const tid = topicMatch?.params?.topicId;
    if (tid) {
      api.get(`/topics/${tid}`).then((r) => {
        setCoachTopic({ id: tid, name: r.data?.name || null });
      }).catch(() => setCoachTopic({ id: tid, name: null }));
    } else {
      setCoachTopic({ id: null, name: null });
    }
  }, [topicMatch]);

  // Bridge: any page can dispatch a `pm-open-coach` custom event to open the
  // drawer with a pre-filled first message + optional topic scope. Used by
  // the "Ask the coach about this" button inside question explanations.
  useEffect(() => {
    const onOpenCoach = (e) => {
      const { prefill, topicId, topicName } = e.detail || {};
      if (topicId) setCoachTopic({ id: topicId, name: topicName || null });
      setCoachPrefill(prefill || "");
      setCoachOpen(true);
    };
    window.addEventListener("pm-open-coach", onOpenCoach);
    return () => window.removeEventListener("pm-open-coach", onOpenCoach);
  }, []);

  // Auto-ping streak once per session on mount
  useEffect(() => {
    pingStudy();
  }, [pingStudy]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b" data-testid="app-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-btn"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <button
              onClick={() => nav("/app")}
              className="flex items-center gap-2 group"
              data-testid="brand-logo"
            >
              <img
                src="/picturemed-logo.png"
                alt="PictureMed"
                className="h-10 w-auto"
                data-testid="brand-logo-img"
              />
            </button>
          </div>

          <button
            onClick={() => setSearchOpen(true)}
            className="hidden md:flex items-center gap-3 px-4 py-2 rounded-full transition-colors"
            style={{ background: "var(--muted)" }}
            data-testid="open-search-btn"
          >
            <Search size={16} style={{ color: "var(--text-muted)" }} />
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>Search topics…</span>
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--paper)", color: "var(--text-muted)" }}>⌘K</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2"
              onClick={() => setSearchOpen(true)}
              data-testid="mobile-search-btn"
            >
              <Search size={20} />
            </button>
            <CreditChip />
            <StreakChip />
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "var(--muted)" }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ background: "var(--accent)" }}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="text-sm font-medium">{user?.name || "You"}</span>
            </div>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/50" data-testid="logout-btn" title="Logout">
              <LogOut size={18} />
            </button>
            <button
              onClick={toggle}
              className="p-2 rounded-lg hover:bg-white/50"
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Toggle color theme"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto w-full flex flex-1">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block w-56 shrink-0 border-r py-8 px-4`} style={{ borderColor: "var(--border)" }}>
          <nav className="space-y-1" data-testid="sidebar-nav">
            {navItems.filter((it) => !it.adminOnly || (user?.email || "").toLowerCase() === ADMIN_EMAIL).map((it) => {
              const gated = it.planGate && features && !features[it.planGate];
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? "text-white"
                        : "hover:bg-white/70"
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: "var(--primary)" } : { color: "var(--text-secondary)" }}
                >
                  <it.icon size={18} />
                  <span className="flex-1">{it.label}</span>
                  {gated && (
                    <span
                      title="Plus feature — tap to see the upgrade"
                      className="inline-flex items-center"
                      style={{ color: "var(--accent)" }}
                      data-testid={`nav-lock-${it.testid}`}
                    >
                      <Lock size={12} />
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 px-4 md:px-8 py-8 page-anim">{children}</main>
      </div>

      {searchOpen && <SearchBar onClose={() => setSearchOpen(false)} />}

      {/* Global Study Coach floating button */}
      <button
        onClick={() => setCoachOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110"
        style={{ background: "linear-gradient(135deg,#2A5CE0,#7B4DFF)" }}
        data-testid="open-coach-btn"
        aria-label="Open Study Coach"
        title="Ask the Study Coach (0.25 credit / message)"
      >
        <Sparkles size={22} color="white" />
      </button>
      <CoachDrawer
        open={coachOpen}
        onClose={() => { setCoachOpen(false); setCoachPrefill(""); }}
        topicId={coachTopic.id}
        topicName={coachTopic.name}
        prefill={coachPrefill}
      />
    </div>
  );
}
